/**
 * The signed-in user's notification inbox for the header bell.
 *
 * GET /api/notifications/me with NO appId — the inbox is device-scoped, so omitting the filter returns
 * notifications delivered to ANY of the user's devices, across every app (Hub, Task, HR, …). That is
 * exactly what the console bell should show.
 *
 * App identity (icon + name) comes from the Application Catalogue, so the bell never hardcodes a list
 * of apps and can't drift from what actually ships.
 *
 * Because the history is per-device, one logical push appears once PER DEVICE the user has registered
 * (web, Android, iOS, per app). The hook collapses those copies into a single row — see dedupe().
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useNotificationInboxService,
  useApplicationCatalogueService,
  type NotificationInboxItem,
  type AppManifest,
} from '@actschurch/ccms-sdk';

/** How often the bell re-checks for new notifications while the console is open. */
const POLL_MS = 60_000;

/**
 * How far back the bell looks. The endpoint is uncapped — it returns EVERY push ever addressed to the
 * user's devices — which is right for a mobile client syncing its history, but would have the header
 * dragging down years of notifications on every poll. A rolling window keeps the payload bounded.
 */
const WINDOW_DAYS = 30;

/** Cap what we render, independent of what came back. */
const MAX_ITEMS = 50;

/**
 * A push is fanned out once per registered device (web, Android, iOS — Hub and every spoke), producing
 * one AppPushMessage row each. The bell should show ONE logical notification, so rows with the same
 * content created within this window are collapsed into a single entry. The window absorbs the few
 * seconds the dispatcher takes to walk the device list without merging a genuinely re-sent
 * notification hours later.
 */
const DEDUP_WINDOW_MS = 2 * 60 * 1000;

export interface InboxApp {
  appId: string;
  name: string;
  iconUrl?: string | null;
}

/** One logical notification: a representative row plus the messageIds of every per-device copy. */
export type InboxItem = NotificationInboxItem & {
  /** Every underlying copy (self included) — mark-read must touch them all or a hidden unread copy resurrects the badge. */
  duplicateIds: number[];
};

/** The packed yyyyMMddHHmmss stamp as epoch millis (0 when malformed). */
function stampToMillis(packed?: number | null): number {
  const s = String(packed ?? '');
  if (s.length !== 14) return 0;
  const d = new Date(
    Number(s.slice(0, 4)), Number(s.slice(4, 6)) - 1, Number(s.slice(6, 8)),
    Number(s.slice(8, 10)), Number(s.slice(10, 12)), Number(s.slice(12, 14)),
  );
  const t = d.getTime();
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Collapse per-device fan-out copies (same title+body, near-same time) into one row each.
 *
 * The device set is the tie-breaker: one logical push targets each device ONCE, so a group never
 * holds two rows for the same device. A second row for a device already in the group is a genuinely
 * NEW notification that happens to repeat the content (the same test push sent twice in a minute) —
 * it must start its own group, or it would inherit the old group's read state and never surface as
 * new.
 */
function dedupe(desc: NotificationInboxItem[]): InboxItem[] {
  const kept: (InboxItem & { _key: string; _at: number; _devices: Set<string> })[] = [];
  for (const item of desc) {
    const key = `${item.title}\u0000${item.body}`;
    const at = stampToMillis(item.createdDateTime);
    const device = (item.deviceId || '').toLowerCase();
    // A row with no device or an unreadable stamp can't be PROVEN to be a copy — keep it separate
    // and never merge into it (`_devices.size > 0` skips groups anchored on such a row). Worst case
    // a duplicate shows twice; merging on a guess could hide a real notification.
    const prev = device && at > 0
      ? kept.find(
          (k) =>
            k._key === key &&
            k._at > 0 &&
            k._devices.size > 0 &&
            Math.abs(k._at - at) <= DEDUP_WINDOW_MS &&
            !k._devices.has(device)
        )
      : undefined;
    if (!prev) {
      kept.push({
        ...item,
        duplicateIds: [item.messageId],
        _key: key,
        _at: at,
        _devices: new Set(device ? [device] : []),
      });
      continue;
    }
    prev.duplicateIds.push(item.messageId);
    prev._devices.add(device);
    // Reading ANY copy (e.g. on a phone) means the user has seen the notification.
    if (item.readAt && !prev.readAt) prev.readAt = item.readAt;
    // Prefer a copy that knows its app / image over one that doesn't.
    if (!prev.appId && item.appId) prev.appId = item.appId;
    if (!prev.imageUrl && item.imageUrl) prev.imageUrl = item.imageUrl;
  }
  return kept.map(({ _key, _at, _devices, ...rest }) => rest);
}

export function useNotificationInbox() {
  const inboxService = useNotificationInboxService();
  const catalogueService = useApplicationCatalogueService();

  const [items, setItems] = useState<NotificationInboxItem[]>([]);
  const [apps, setApps] = useState<AppManifest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Guards against a slow response from a previous fetch overwriting a newer one.
  const requestId = useRef(0);

  const fetchInbox = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      // No appId → every app the user has a device for. `since` bounds the (otherwise uncapped) history.
      const since = new Date(Date.now() - WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();
      const res = await inboxService.fetchMyInbox(undefined, since);
      if (id !== requestId.current) return;
      setItems(res?.items ?? []);
    } catch (e: any) {
      if (id !== requestId.current) return;
      setError(e?.message || 'Failed to load notifications');
      setItems([]);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [inboxService]);

  // The catalogue is small and static — fetch once for icons/names.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const list = await catalogueService.getCatalogue();
        if (active) setApps(list || []);
      } catch {
        // Non-fatal: rows fall back to a generic icon and the raw appId.
        if (active) setApps([]);
      }
    })();
    return () => { active = false; };
  }, [catalogueService]);

  useEffect(() => {
    fetchInbox();
    const t = setInterval(fetchInbox, POLL_MS);
    return () => clearInterval(t);
  }, [fetchInbox]);

  /**
   * Resolve an appId to its catalogue identity. When the app isn't known, `name` is EMPTY — callers
   * omit the line rather than printing a placeholder like "Unknown app", which is noise, not information.
   */
  const appFor = useCallback(
    (appId?: string | null): InboxApp => {
      const id = appId || '';
      const app = apps.find((a) => a.appId?.toLowerCase() === id.toLowerCase());
      return {
        appId: id,
        name: app?.displayName || app?.shortName || id || '',
        iconUrl: app?.iconUrl || null,
      };
    },
    [apps]
  );

  // Newest first — the bell is a "what just happened" surface, not an archive. Per-device fan-out
  // copies are collapsed AFTER sorting (dedupe merges into the newest copy) and capped after that,
  // so duplicates never eat into the render budget.
  const sorted = useMemo(
    () =>
      dedupe(
        [...items].sort((a, b) => Number(b.createdDateTime || 0) - Number(a.createdDateTime || 0))
      ).slice(0, MAX_ITEMS),
    [items]
  );

  const unread = useMemo(() => sorted.filter((i) => !i.readAt), [sorted]);

  const markRead = useCallback(
    async (messageIds: number[]) => {
      if (messageIds.length === 0) return;
      // Optimistic: the bell should feel instant. A failed call is re-corrected by the next poll.
      const now = new Date().toISOString();
      setItems((prev) => prev.map((i) => (messageIds.includes(i.messageId) ? { ...i, readAt: now } : i)));
      try {
        await inboxService.markRead({ messageIds });
      } catch {
        await fetchInbox();
      }
    },
    [inboxService, fetchInbox]
  );

  const markAllRead = useCallback(async () => {
    if (unread.length === 0) return;
    const now = new Date().toISOString();
    setItems((prev) => prev.map((i) => (i.readAt ? i : { ...i, readAt: now })));
    try {
      await inboxService.markRead({ all: true });
    } catch {
      await fetchInbox();
    }
  }, [inboxService, fetchInbox, unread.length]);

  return {
    items: sorted,
    unreadCount: unread.length,
    loading,
    error,
    refresh: fetchInbox,
    markRead,
    markAllRead,
    appFor,
  };
}
